import React, { useState } from 'react';
import { Box, Typography, Grid, Button, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { downloadFile } from '../../../helpers';
import axios from 'axios';
import phasesImg1 from '../../../assets/homeowner-portal/phases_pre_contruction.webp';
import phasesImg2 from '../../../assets/homeowner-portal/phases_form_to_frame.webp';
import phasesImg3 from '../../../assets/homeowner-portal/phases_frame_to_drywall.webp';
import phasesImg4 from '../../../assets/homeowner-portal/phases_drywall_to_clean.webp';
import phasesImg5 from '../../../assets/homeowner-portal/phases_clean_to_close.webp';
import phasesImg6 from '../../../assets/homeowner-portal/phases_welcome_home.webp';

type StepMeta = {
  title: string;
  description: string;
};

const phaseImages = [phasesImg1, phasesImg2, phasesImg3, phasesImg4, phasesImg5, phasesImg6];
const phasesOfConstructionLink =
  'https://prodbuilderlinqstorage.blob.core.windows.net/homeowner-portal-docs/Phases_of_Construction_Non_Thrive_Dec2023.pdf';

const steps: StepMeta[] = [
  {
    title: 'Phase 1: Contract → Pre-Construction',
    description:
      "This is where the dream begins! You'll work with your Sales Professional to select your floor plan, homesite, and structural options before officially signing your contract. Once your contract is ratified, you'll gain access to BuilderLinq and finalize your design choices. Behind the scenes, we start the permitting process, order materials, and prepare your homesite. You'll also meet your Construction Professional during the Kickoff Meeting to get a feel for the exciting journey ahead."
  },
  {
    title: 'Phase 2: Foundation → Form to Frame',
    description:
      "Things start taking shape as your home's foundation is carefully laid out. Surveyors and contractors prepare the land, install plumbing, and pour the slab after all inspections are approved. It's the first physical step of your home coming to life, setting the stage for everything that follows."
  },
  {
    title: 'Phase 3: Framing & Rough-Ins → Frame to Drywall',
    description:
      "Now your home really begins to look like a home! Our team constructs the framework and installs critical systems like HVAC, plumbing, electrical, and gas. You'll be invited to a Pre-Drywall Meeting where you can walk through and see the progress up close. After inspections and quality checks, we move on to insulation and getting ready to close up the walls."
  },
  {
    title: 'Phase 4: Interior & Exterior Build Out → Drywall to Clean',
    description:
      "This is one of the most exciting phases as your vision comes to life both inside and out. Drywall goes up, cabinets and trim are installed, and the interior finishes like flooring, countertops, fixtures, and appliances begin to transform your space. Meanwhile, the exterior is polished with siding, landscaping, and utility connections. We're nearing the finish line!"
  },
  {
    title: 'Phase 5: Pursuit of Excellence → Clean to Close',
    description:
      "Your home gets a final polish and thorough quality inspection by our expert team. We'll walk through every room and detail to ensure everything meets our standards. You'll attend your Homeowner Orientation to see your finished home and learn how to care for it. This final step is all about ensuring excellence and handing over a home we're proud to deliver—and you're thrilled to call yours."
  },
  {
    title: 'Welcome Home!',
    description:
      "Congratulations—your journey has just begun! Now that you've settled into your beautiful new home, we're still here for you every step of the way. As part of the Stone Martin Builders family, you'll receive helpful tips and tricks, seasonal maintenance reminders, and continued support to make sure your home stays in top shape. You'll also have access to our Homeowners Portal, your go-to resource for learning more about your home's features, warranty information, and connecting with your new community. We're committed to making sure you feel confident, cared for, and completely at home—because this isn't just where you live, it's where life happens. Welcome home!"
  }
];

export const PhasesStepper: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const goToStep = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    setActiveStep(nextIndex);
  };

  const goToPrevious = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setActiveStep(prev => Math.min(steps.length - 1, prev + 1));
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 3, md: 8 } }}>
      <Grid container spacing={2} alignItems="stretch" sx={{ py: 2, mb: 1 }}>
        {/* Left Image Column */}
        <Grid item xs={12} md={6} lg={4}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: '300px', md: '500px' },
              overflow: 'hidden',
              borderRadius: 2
            }}
          >
            {phaseImages.map((imageSrc, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${imageSrc})`,
                  backgroundSize: 'cover',
                  backgroundPosition: '70% center',
                  backgroundRepeat: 'no-repeat',
                  opacity: activeStep === index ? 1 : 0,
                  transition: 'opacity 0.4s ease-in-out',
                  zIndex: activeStep === index ? 1 : 0
                }}
              />
            ))}
          </Box>
        </Grid>

        {/* Middle Stepper Column */}
        <Grid
          item
          xs={12}
          md={1}
          sx={{
            position: 'relative',
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2,
                height: 160,
                bgcolor: 'grey.300',
                borderRadius: 1,
                top: 0
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              {steps.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    width: activeStep === index ? 28 : 10,
                    height: activeStep === index ? 28 : 10,
                    borderRadius: '50%',
                    backgroundColor: 'primary.dark',
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: activeStep === index ? 2 : 'none',
                    transition: 'all 200ms ease'
                  }}
                  onClick={() => goToStep(index)}
                >
                  {activeStep === index ? index + 1 : null}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2,
                height: 160,
                bgcolor: 'grey.300',
                borderRadius: 1,
                bottom: 0
              }}
            />
          </Box>
        </Grid>

        {/* Text Content with Navigation */}
        <Grid item xs={12} md={5} lg={7}>
          <Box
            sx={{
              height: { xs: 'auto', md: '500px' },
              minHeight: { xs: '350px', md: '500px' },
              display: 'flex',
              flexDirection: 'column',
              mb: { xs: 1, md: 0 },
              pb: { xs: 1, md: 0 }
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'normal', md: 'space-between' },
                py: { xs: 0, md: 2, lg: 12 }
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Welcome to the Stone Martin Family!
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
                  {steps[activeStep].title}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {steps[activeStep].description}
                </Typography>
                {activeStep === 0 && (
                  <Button
                    variant="contained"
                    disabled={isDownloading}
                    onClick={async () => {
                      try {
                        setIsDownloading(true);
                        const res = await axios.get(phasesOfConstructionLink, {
                          responseType: 'blob'
                        });
                        downloadFile(res.data, 'Phases_of_Construction.pdf');
                      } finally {
                        setIsDownloading(false);
                      }
                    }}
                    sx={{ width: { xs: '100%', md: '275px' } }}
                  >
                    Phases of Construction
                  </Button>
                )}
              </Box>

              {/* Navigation Chevrons - Integrated with content */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mt: 0
                }}
              >
                <IconButton
                  onClick={goToPrevious}
                  disabled={activeStep === 0}
                  sx={{
                    backgroundColor: 'primary.dark',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark'
                    },
                    '&:disabled': { backgroundColor: 'grey.300', color: 'grey.500' }
                  }}
                >
                  <ChevronLeft />
                </IconButton>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 20, textAlign: 'center' }}
                >
                  {activeStep + 1} of {steps.length}
                </Typography>

                <IconButton
                  onClick={goToNext}
                  disabled={activeStep === steps.length - 1}
                  sx={{
                    backgroundColor: 'primary.dark',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark'
                    },
                    '&:disabled': { backgroundColor: 'grey.300', color: 'grey.500' }
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
